import type { CandlestickData, Time } from 'lightweight-charts';

export interface MacdPoint {
  time: Time;
  macd: number | null;
  signal: number | null;
  hist: number | null;
}

export interface StochPoint {
  time: Time;
  k: number | null;
  d: number | null;
}

/** Exponential moving average — seeded with the first value for a smooth warm-up. */
function ema(values: number[], period: number): number[] {
  const out: number[] = new Array(values.length).fill(0);
  if (values.length === 0) return out;
  const k = 2 / (period + 1);
  let prev = values[0];
  out[0] = prev;
  for (let i = 1; i < values.length; i += 1) {
    prev = values[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

/** Simple moving average — returns null until the window is full. */
function sma(values: number[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i += 1) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

function round(value: number, digits = 4): number {
  return Number(value.toFixed(digits));
}

/** MACD (fast, slow, signal) — histogram = MACD − signal line. */
export function computeMacd(
  data: CandlestickData<Time>[],
  fast = 12,
  slow = 26,
  signalPeriod = 9,
): MacdPoint[] {
  const closes = data.map((d) => d.close);
  const fastEma = ema(closes, fast);
  const slowEma = ema(closes, slow);
  const macdLine = closes.map((_, i) => fastEma[i] - slowEma[i]);
  const signalLine = ema(macdLine, signalPeriod);

  return data.map((d, i) => ({
    time: d.time,
    macd: round(macdLine[i]),
    signal: round(signalLine[i]),
    hist: round(macdLine[i] - signalLine[i]),
  }));
}

/** Stochastic oscillator (kPeriod, dPeriod, smoothing). Values are 0–100. */
export function computeStochastic(
  data: CandlestickData<Time>[],
  kPeriod = 9,
  dPeriod = 3,
): StochPoint[] {
  const rawK: number[] = [];
  for (let i = 0; i < data.length; i += 1) {
    const start = Math.max(0, i - kPeriod + 1);
    let highest = -Infinity;
    let lowest = Infinity;
    for (let j = start; j <= i; j += 1) {
      if (data[j].high > highest) highest = data[j].high;
      if (data[j].low < lowest) lowest = data[j].low;
    }
    rawK.push(highest === lowest ? 50 : ((data[i].close - lowest) / (highest - lowest)) * 100);
  }

  const kLine = sma(rawK, dPeriod);
  const kFilled = kLine.map((v) => v ?? 0);
  const dLine = sma(kFilled, dPeriod);

  return data.map((d, i) => ({
    time: d.time,
    k: kLine[i] == null ? null : round(kLine[i] as number, 2),
    d: dLine[i] == null ? null : round(dLine[i] as number, 2),
  }));
}